import { Box, Text, VStack } from '@chakra-ui/react';
import {
  type MentionBoostInsightsDto,
  type MentionBoostInsightsView,
  MENTION_BOOST_TREND_MIN_POINTS,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { Sparkline } from '@/components/charts/Sparkline';

interface BoostInsightsTrendChartProps {
  insights: MentionBoostInsightsDto;
  view: MentionBoostInsightsView;
}

/** Maps the per-day series to the Sparkline's bare value shape. */
function toSparklineData(trend: MentionBoostInsightsDto['trend']) {
  return trend.map((point) => ({ value: point.value }));
}

/**
 * The head-metric curve over the boost's life: per-day sales (conversion) or
 * clicks (traffic), so a brand reads momentum or fade at a glance. Gated on the
 * shared honest threshold — the curve only draws once there are enough real
 * daily points; below it a neutral line, never an interpolated shape. The title
 * names the metric the view leads with so the curve is never ambiguous.
 */
export function BoostInsightsTrendChart({ insights, view }: BoostInsightsTrendChartProps) {
  const { t } = useTranslation();

  const title =
    view === 'conversion'
      ? t('mentions.boost.insights.trend.salesTitle')
      : t('mentions.boost.insights.trend.clicksTitle');

  if (insights.trend.length < MENTION_BOOST_TREND_MIN_POINTS) {
    return (
      <VStack align="stretch" gap={1} data-testid="boost-insights-trend-empty">
        <Text fontSize="sm" fontWeight="medium" color="gray.900">
          {title}
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {t('mentions.boost.insights.trend.notEnough')}
        </Text>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" gap={2} data-testid="boost-insights-trend">
      <Text fontSize="sm" fontWeight="medium" color="gray.900">
        {title}
      </Text>
      <Box position="relative" width="full" height="120px">
        <Sparkline inline data={toSparklineData(insights.trend)} type="area" color="blue.600" />
      </Box>
    </VStack>
  );
}
